import ZonePricetypesCollection from './ZonePricetypesCollection';
import PreselectedPricetypeQuantities from './PreselectedPricetypeQuantities';
import ZoneSelectorCollection from './zoneSelector/ZoneSelectorCollection';
import WheelchairQuantitySelect from './WheelchairQuantitySelect';
import { flattenPricetypeQuantities, stringifySpecialRequests } from './TicketSelectorUtils';
import Messenger from '../../tnew/ui/Messenger';
import dom from '../../utils/dom';
import FormFieldsComponent from './components/FormFieldsComponent';

/** @typedef {import("./types").PriceTypeQuantity} PriceTypeQuantity */
/** @typedef {import("./types").PricetypeQuantities} PricetypeQuantities */
/** @typedef {import("./types").TicketSelectorOptions} TicketSelectorOptions */

function getOptions(options = {}) {
  /** @type {TicketSelectorOptions} */
  const defaults = {
    selectedZoneId: '',
    restoringPricetypeQuantitiesForSelectedZone: undefined,
    preselectedPricetypeQuantities: undefined,
  };

  const result = { ...defaults, ...options };

  result.selectedZoneId = result.selectedZoneId || '';

  return result;
}

class TicketSelector {
  /**
   * @param {HTMLFormElement} form
   * @param {Partial<TicketSelectorOptions>} options_
   */
  constructor(form, options_ = {}) {
    this.form = form;

    this.messenger = this.initMessenger();

    const options = getOptions(options_);

    const {
      selectedZoneId,
      preselectedPricetypeQuantities,
      restoringPricetypeQuantitiesForSelectedZone,
    } = options;

    this.zones = new ZoneSelectorCollection(this.form);

    this.pricetypes = new ZonePricetypesCollection(this.form);
    this.wheelchairSelect = new WheelchairQuantitySelect(this.find('.tn-wheel-chair-quantity'));

    this.chooseZoneGroupPrompt = this.find('#instructions-choose-zone-group');
    this.chooseZonePrompt = this.find('#instructions-choose-zone');
    this.seatingOptionsContainer = this.find('#tn-fieldset-seating-options');
    this.btnSubmit = this.find('button[type="submit"]');

    this.zones.on('update', this.onZoneChange);
    this.pricetypes.on('change', this.updateUi);

    if (preselectedPricetypeQuantities) {
      this.pricetypeQuantities = new PreselectedPricetypeQuantities(preselectedPricetypeQuantities);
    } else {
      this.pricetypeQuantities = this.pricetypes;
    }

    this.zones.selectZone(options.selectedZoneId);

    this.zones.autoSelectSingleZone();

    if (
      !preselectedPricetypeQuantities
      && restoringPricetypeQuantitiesForSelectedZone
      && selectedZoneId
      && selectedZoneId === this.zones.getSelectedZoneId()
    ) {
      this.pricetypes.restoreSelectedQuantities(restoringPricetypeQuantitiesForSelectedZone);
    }

    this.updateUi();
  }

  initMessenger() {
    const messageContainer = document.createElement('div');

    const messenger = new Messenger(messageContainer);

    this.form.insertAdjacentElement('afterbegin', messageContainer);

    return messenger;
  }

  addErrorMessage(message, focus = false) {
    this.messenger.addErrorMessage(message, focus);
  }

  clearMessages(message) {
    this.messenger.clearMessages(message);
  }

  /**
   * @param {string} selector
   */
  find(selector) {
    return this.form.querySelector(selector);
  }

  onZoneChange = (zoneId) => {
    this.pricetypes.setCurrentZoneId(zoneId);
    this.updateUi();
  };

  updateUi = () => {
    const zoneGroupId = this.zones.getSelectedZoneGroupId();
    const zoneId = this.zones.getSelectedZoneId();
    const pricetypes = this.pricetypeQuantities.getData();

    const isZoneGroupSelected = !!zoneGroupId;
    const isZoneSelected = !!zoneId;

    if (this.chooseZoneGroupPrompt) {
      if (zoneGroupId) {
        dom.hide(this.chooseZoneGroupPrompt);
      } else {
        dom.unhide(this.chooseZoneGroupPrompt);
      }
    }

    if (this.chooseZonePrompt) {
      if (isZoneGroupSelected && !isZoneSelected) {
        dom.unhide(this.chooseZonePrompt);
      } else {
        dom.hide(this.chooseZonePrompt);
      }
    }

    if (this.seatingOptionsContainer) {
      if (isZoneSelected) {
        dom.unhide(this.seatingOptionsContainer);
      } else {
        dom.hide(this.seatingOptionsContainer);
      }
    }

    this.wheelchairSelect.updateMaxQuantity(pricetypes.totalQuantitySelected);

    const isSubmitButtonEnabled = this.getIsSubmitButtonEnabled();

    if (isSubmitButtonEnabled) {
      this.btnSubmit.classList.remove('disabled');
      this.btnSubmit.removeAttribute('aria-disabled');
    } else {
      this.btnSubmit.classList.add('disabled');
      this.btnSubmit.setAttribute('aria-disabled', 'true');
    }
  };

  getIsSubmitButtonEnabled() {
    const zoneId = this.zones.getSelectedZoneId();

    if (!zoneId) {
      return false;
    }

    const pricetypes = this.pricetypeQuantities.getData();

    const isMaximumQuantityExceeded = pricetypes.totalQuantitySelected > pricetypes.maxQuantity;

    if (isMaximumQuantityExceeded) {
      return false;
    }

    if (pricetypes.isEditablePriceAvailable) {
      return true;
    }

    const isValidQuantitySelected = pricetypes.isSelectionValid && pricetypes.totalQuantitySelected > 0;

    if (isValidQuantitySelected) {
      return true;
    }

    return false;
  }

  getRequestData() {
    const {
      allPricetypeQuantities,
      totalQuantitySelected: totalQuantity,
    } = this.pricetypeQuantities.getData();

    const zoneId = this.zones.getSelectedZoneId();
    const specialRequests = this.getSpecialRequests(totalQuantity);
    const quantiyArr = flattenPricetypeQuantities(allPricetypeQuantities);

    return {
      allPricetypeQuantities,
      totalQuantity,
      zoneId,
      quantiyArr,
      specialRequests,
    };
  }

  getIsPriceTypeSelectionValid() {
    const pricetypes = this.pricetypeQuantities.getData();

    if (!pricetypes.totalQuantitySelected) {
      return false;
    }

    if (pricetypes.isEditablePriceAvailable) {
      if (!pricetypes.isSelectionValid) {
        return false;
      }
    }

    return true;
  }

  preFormSubmit = (event) => {
    if (!this.getIsSubmitButtonEnabled()) {
      event.preventDefault();

      return;
    }

    this.messenger.clearMessages();

    if (!this.getIsPriceTypeSelectionValid()) {
      event.preventDefault();

      this.messenger.addErrorMessage(FormFieldsComponent.priceAndQuantityFieldsValidationMessage, true);
      this.pricetypes.validateEditablePriceInputs();

      const { formButtonHandler } = window.tnew.handlers;
      window.setTimeout(() => formButtonHandler.resetButtonStyling());

      return;
    }

    this.onFormSubmit(event);
  };

  onFormSubmit = () => {
    const {
      totalQuantity, zoneId, quantiyArr, specialRequests,
    } = this.getRequestData();

    this.find('#tn-form-event-price-type-id').setAttribute('value', quantiyArr.join(','));
    this.find('#tn-form-event-quantity').setAttribute('value', totalQuantity);
    this.find('#tn-form-event-zone-id').setAttribute('value', zoneId);

    const specialRequestsInput = this.find('#tn-form-event-special-requests');
    if (specialRequestsInput) {
      const specialRequestsString = stringifySpecialRequests(specialRequests);

      specialRequestsInput.setAttribute('value', specialRequestsString);
    }
  };

  /**
   * @param {number} totalQuantity
   */
  getSpecialRequests(totalQuantity) {
    const checkboxContiguous = this.find('#SpecialRequests_IsContiguousSeatsRequested');
    const checkboxNoStairs = this.find('#SpecialRequests_IsNoStairsRequested');
    const selectAislePreference = this.find('#SeatingOptions_AisleOptions');
    const numWheelchairSeats = this.wheelchairSelect.getSelectedQuantity();
    const requests = {};

    if (checkboxContiguous && checkboxContiguous.checked) {
      requests.ContiguousSeats = totalQuantity;
    } else {
      requests.ContiguousSeats = 1;
    }

    if (checkboxNoStairs && checkboxNoStairs.checked) {
      requests.NoStairs = 'Y';
    }

    if (selectAislePreference && selectAislePreference.value) {
      requests.AisleSeat = selectAislePreference.value;
    }

    if (numWheelchairSeats > 0) {
      requests.WheelChairSeats = numWheelchairSeats;
    }

    return requests;
  }

  destroy() {
    this.zones.destroy();
    this.pricetypes.destroy();
    this.pricetypeQuantities = null;
    this.form.removeEventListener('submit', this.preFormSubmit);
  }
}

export default TicketSelector;
